import { Component, computed, inject, Input, ViewChild } from '@angular/core';
import {
  AvatarComponent,
  BadgeComponent,
  BreadcrumbRouterComponent,
  ButtonModule,
  ColorModeService,
  ContainerComponent,
  DropdownComponent,
  DropdownDividerDirective,
  DropdownHeaderDirective,
  DropdownItemDirective,
  DropdownMenuDirective,
  DropdownToggleDirective,
  HeaderComponent,
  HeaderNavComponent,
  HeaderTogglerDirective,
  NavItemComponent,
  NavLinkDirective,
  ProgressBarDirective,
  ProgressComponent,
  SidebarToggleDirective,
  TextColorDirective,
  ThemeDirective,
  ToasterComponent,
  ToasterPlacement
} from '@coreui/angular-pro';
import { CommonModule, NgStyle, NgTemplateOutlet } from '@angular/common';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { IconDirective } from '@coreui/icons-angular';
import { AppToastComponent } from '../../../views/notifications/toasters/toast-simple/toast.component';
import { ToastService } from '../../../toast.service';
import { BaseService } from '../../../base.service';
import { AutoavatarComponent } from '../../../views/base/autoavatar/autoavatar.component';
import { LoginService } from '../../../views/pages/login/login.service';

import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-default-header',
  templateUrl: './default-header.component.html',
  standalone: true,
  imports: [ContainerComponent, CommonModule, ButtonModule, HeaderTogglerDirective, ToasterComponent, SidebarToggleDirective, IconDirective, HeaderNavComponent, NavItemComponent, NavLinkDirective, RouterLink, RouterLinkActive, NgTemplateOutlet, BreadcrumbRouterComponent, ThemeDirective, DropdownComponent, DropdownToggleDirective, TextColorDirective, AvatarComponent, DropdownMenuDirective, DropdownHeaderDirective, DropdownItemDirective, BadgeComponent, DropdownDividerDirective, ProgressBarDirective, ProgressComponent, NgStyle, AutoavatarComponent]
})
export class DefaultHeaderComponent extends HeaderComponent {

  readonly #colorModeService = inject(ColorModeService);
  readonly colorMode = this.#colorModeService.colorMode;

  readonly colorModes = [
    { name: 'light', text: 'Light', icon: 'cilSun' },
    { name: 'dark', text: 'Dark', icon: 'cilMoon' },
    { name: 'auto', text: 'Auto', icon: 'cilContrast' }
  ];

  readonly icons = computed(() => {
    const currentMode = this.colorMode();
    return this.colorModes.find(mode => mode.name === currentMode)?.icon ?? 'cilSun';
  });

  constructor(
    public router: Router,
    public ToastService: ToastService,
    public BaseService: BaseService,
    public LoginService: LoginService
  ) {
    super();
    this.#colorModeService.localStorageItemName.set('coreui-pro-angular-admin-template-theme-default');
    this.#colorModeService.eventName.set('ColorSchemeChange');
  }

  @Input() sidebarId: string = 'sidebar1';

  public newMessages = [
    {
      id: 0,
      from: 'Jessica Williams',
      avatar: '7.jpg',
      status: 'success',
      title: 'Urgent: System Maintenance Tonight',
      time: 'Just now',
      link: 'apps/email/inbox/message',
      message: 'Attention team, we\'ll be conducting critical system maintenance tonight from 10 PM to 2 AM. Plan accordingly...'
    },
    {
      id: 1,
      from: 'Richard Johnson',
      avatar: '6.jpg',
      status: 'warning',
      title: 'Project Update: Milestone Achieved',
      time: '5 minutes ago',
      link: 'apps/email/inbox/message',
      message: 'Kudos on hitting sales targets last quarter! Let\'s keep the momentum. New goals, new victories ahead...'
    },
    {
      id: 2,
      from: 'Angela Rodriguez',
      avatar: '5.jpg',
      status: 'danger',
      title: 'Social Media Campaign Launch',
      time: '1:52 PM',
      link: 'apps/email/inbox/message',
      message: 'Exciting news! Our new social media campaign goes live tomorrow. Brace yourselves for engagement...'
    },
    {
      id: 3,
      from: 'Jane Lewis',
      avatar: '4.jpg',
      status: 'info',
      title: 'Inventory Checkpoint',
      time: '4:03 AM',
      link: 'apps/email/inbox/message',
      message: 'Team, it\'s time for our monthly inventory check. Accurate counts ensure smooth operations. Let\'s nail it...'
    },
    {
      id: 4,
      from: 'Ryan Miller',
      avatar: '4.jpg',
      status: 'info',
      title: 'Customer Feedback Results',
      time: '3 days ago',
      link: 'apps/email/inbox/message',
      message: 'Our latest customer feedback is in. Let\'s analyze and discuss improvements for an even better service...'
    }
  ];

  public newNotifications = [
    { id: 0, title: 'New user registered', icon: 'cilUserFollow', color: 'success' },
    { id: 1, title: 'User deleted', icon: 'cilUserUnfollow', color: 'danger' },
    { id: 2, title: 'Sales report is ready', icon: 'cilChartPie', color: 'info' },
    { id: 3, title: 'New client', icon: 'cilBasket', color: 'primary' },
    { id: 4, title: 'Server overloaded', icon: 'cilSpeedometer', color: 'warning' }
  ];

  public newStatus = [
    { id: 0, title: 'CPU Usage', value: 25, color: 'info', details: '348 Processes. 1/4 Cores.' },
    { id: 1, title: 'Memory Usage', value: 70, color: 'warning', details: '11444GB/16384MB' },
    { id: 2, title: 'SSD 1 Usage', value: 90, color: 'danger', details: '243GB/256GB' }
  ];

  public newTasks = [
    { id: 0, title: 'Upgrade NPM', value: 0, color: 'info' },
    { id: 1, title: 'ReactJS Version', value: 25, color: 'danger' },
    { id: 2, title: 'VueJS Version', value: 50, color: 'warning' },
    { id: 3, title: 'Add new layouts', value: 75, color: 'info' },
    { id: 4, title: 'Angular Version', value: 100, color: 'success' }
  ];

  placement = ToasterPlacement.TopEnd;
  @ViewChild(ToasterComponent) toaster!: ToasterComponent;
  UserDisplayName: any;

  UserData: any;
  Product_Id: any;
  onConnectIntuit(): void {
    this.LoginService.getIntuitConnectURL().subscribe({
      next: (res: any) => {
        if (res?.APIStatus?.IsSuccess && res?.APIStatus?.Details) {
          this.BaseService.QBIntuitUrl = res.APIStatus.Details;
          console.log("🚀 > onConnectIntuit > this.BaseService.QBIntuitUrl:", this.BaseService.QBIntuitUrl)
        } else {
          console.error('Failed to get Intuit Connect URL:', res);
        }
      },
      error: (err: any) => {
        console.error('Error connecting to Intuit:', err);
      }
    });
  }

  ngOnInit() {
    setTimeout(() => {
      const Product_Id = localStorage.getItem('Product_Id') as string;
      this.Product_Id = Product_Id;
    }, 1000);
    this.onConnectIntuit();

    this.ToastService.toastState.subscribe((options) => {
      this.toaster.addToast(AppToastComponent, { ...options });
    });
    const userdata = localStorage.getItem('UserInfo') as string;
    if (userdata != 'undefined' && userdata != null) {
      let User = JSON.parse(localStorage.getItem('UserInfo') as string);
      this.UserData = User;
      this.UserDisplayName = User?.userDisplayName;
    }
  }


  extractInitials(name: string): string {
    const nameParts = name.split(' ');
    const initials = nameParts.map(part => part.charAt(0)).join('').substring(0, 2);
    return initials.toUpperCase();
  }

  // logout(){
  //   localStorage.clear();
  //   this.router.navigate(['/login']);
  // }

  get showBackToApp(): boolean {
    return environment.backToAppProductIds.includes(this.Product_Id);
  }

  goToPortal() {
    const Product_Id = localStorage.getItem('Product_Id') as string;

    console.log("🚀 > goToPortal > Product_Id:", Product_Id)

    if (environment.backToAppProductIds.includes(Product_Id)) {
      this.BaseService.getIntuitConnectURL(Product_Id).subscribe({
        next: (response) => {
          localStorage.clear();
          window.location.href = response?.APIStatus?.Details; // Assuming response has 'url' property
        },
        error: (err) => {
          console.error('Error getting connect URL:', err);
          // Optionally show a toast or handle error
        }
      });
    }
  }

}
